/**
 * Search Date Range Picker Component
 *
 * UI component for selecting date ranges in search.
 * Uses the headless useCalendar hook for logic.
 *
 * Props:
 * - onDateRangeSelect: Callback when date range is selected
 * - onClose: Callback to close the picker
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCalendar } from './hooks/useCalendar';
import DateRangeStrategy from './strategies/DateRangeStrategy';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function SearchDateRangePicker({ onDateRangeSelect, onClose }) {
  // Create strategy instance
  const strategy = new DateRangeStrategy(90); // Max 90 days for search

  // Use headless calendar hook
  const calendar = useCalendar({
    strategy,
    onSelectionComplete: (dates) => {
      if (dates.length === 2) {
        onDateRangeSelect?.(dates[0], dates[1]);
      }
    }
  });

  /**
   * Get CSS classes for a calendar day
   */
  const getDayClasses = (date) => {
    const baseClasses = 'aspect-square p-2 text-sm rounded-lg transition-colors';
    const isSelected = calendar.isDateSelected(date);
    const isInRange = calendar.isDateInRange(date);
    const isAvailable = calendar.isDateAvailable(date);
    const isInCurrentMonth = calendar.isDateInCurrentMonth(date);

    let classes = baseClasses;

    if (!isAvailable) {
      classes += ' text-gray-300 cursor-not-allowed';
    } else if (isSelected) {
      classes += ' bg-gray-900 text-white font-semibold cursor-pointer';
    } else if (isInRange) {
      classes += ' bg-gray-100 cursor-pointer hover:bg-gray-200';
    } else if (isInCurrentMonth) {
      classes += ' hover:bg-gray-50 cursor-pointer';
    } else {
      classes += ' text-gray-400 cursor-pointer';
    }

    return classes;
  };

  /**
   * Handle date click
   */
  const handleDateClick = (date) => {
    if (calendar.isDateAvailable(date)) {
      calendar.selectDate(date);
    }
  };

  /**
   * Handle clear button
   */
  const handleClear = () => {
    calendar.clearSelection();
  };

  /**
   * Handle apply button
   */
  const handleApply = () => {
    if (calendar.isSelectionComplete) {
      onClose?.();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={calendar.navigateToPrevMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <h3 className="font-semibold text-gray-900">
          {calendar.currentMonth.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
          })}
        </h3>

        <button
          onClick={calendar.navigateToNextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
          aria-label="Next month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Weekday Labels */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAY_LABELS.map(day => (
          <div
            key={day}
            className="text-center text-xs font-semibold text-gray-600 p-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {calendar.daysInMonth.map((date, idx) => (
          <button
            key={idx}
            onClick={() => handleDateClick(date)}
            disabled={!calendar.isDateAvailable(date)}
            className={getDayClasses(date)}
            aria-label={date.toLocaleDateString('en-US')}
          >
            {date.getDate()}
          </button>
        ))}
      </div>

      {/* Selection Display */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">{calendar.selectionDisplay}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={handleClear}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
        >
          Clear
        </button>
        <button
          onClick={handleApply}
          disabled={!calendar.isSelectionComplete}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${
            calendar.isSelectionComplete
              ? 'bg-gray-900 text-white hover:bg-gray-800'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

export default SearchDateRangePicker;
